import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, X, Pencil } from 'lucide-react';
import EditFinancialModal from './components/EditFinancialModal';
import AddIncomeModal from './components/AddIncomeModal';
import AddExpenseModal from './components/AddExpenseModal';
import EnhancedFinancialsTable from './EnhancedFinancialsTable';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedFinancials.css';
import { supabase } from '../../../lib/supabase';
import type { DevFinancial } from '../../../types/financial';
import type { FinancialFilter, SortConfig, FinancialSortField } from '../../../types/financial-filters';
import { formatTextWithLinks } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';
import { useFinancialFilters } from '../../../hooks/useFinancialFilters';
import { format } from 'date-fns';
import '../../../components/common/EnhancedTable/EnhancedTableFix.css';

export default function EnhancedFinancials() {
  const { canManageSystemSettings, isAdmin } = usePermissions();
  const queryClient = useQueryClient();
  
  // State for modals
  const [selectedFinancial, setSelectedFinancial] = useState<DevFinancial | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddIncomeModalOpen, setIsAddIncomeModalOpen] = useState(false);
  const [isAddExpenseModalOpen, setIsAddExpenseModalOpen] = useState(false);
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<FinancialFilter>({
    community: null,
    supplier: null,
    dateFrom: null,
    dateTo: null
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'date',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [financialYear, setFinancialYear] = useState<string>('all');
  
  // Fetch financial data
  const { data: financials = [], isLoading, error } = useQuery<DevFinancial[]>({
    queryKey: ['financials'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('dev_financials')
          .select('*');

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        return data || [];
      } catch (error) {
        console.error('Error fetching financial data:', error);
        throw error;
      }
    }
  });

  // Generate financial year options
  const financialYearOptions = useMemo(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // If we're in the first half of the calendar year, the current FY started last year
    const currentFY = currentMonth < 6 ? currentYear - 1 : currentYear;
    
    const options = [
      { value: 'all', label: 'All Time' }
    ];

    // Generate last 5 financial years
    for (let i = 0; i < 5; i++) {
      const startYear = currentFY - i;
      const endYear = startYear + 1;
      options.push({
        value: `${startYear}-${endYear}`,
        label: `FY ${startYear}-${endYear}`
      });
    }

    return options;
  }, []);

  // Get date range for financial year
  const getDateRangeForFinancialYear = (fy: string): { from?: Date; to?: Date } => {
    if (fy === 'all') return {};
    
    const [startYear, endYear] = fy.split('-').map(Number);
    
    // Validate years
    if (isNaN(startYear) || isNaN(endYear)) {
      console.error('Invalid financial year format:', fy);
      return {};
    }
    
    // Create dates for financial year (July 1st to June 30th)
    const from = new Date(Date.UTC(startYear, 6, 1)); // July 1st
    const to = new Date(Date.UTC(endYear, 5, 30)); // June 30th
    
    return { from, to };
  };

  // Apply financial year filter
  useEffect(() => {
    if (financialYear !== 'all') {
      const { from, to } = getDateRangeForFinancialYear(financialYear);
      setFilters(prev => ({
        ...prev,
        dateFrom: from || null,
        dateTo: to || null
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        dateFrom: null,
        dateTo: null
      }));
    }
  }, [financialYear]);

  // Extract unique communities and suppliers for filter dropdowns
  const { communities, suppliers } = useMemo(() => {
    const communitiesSet = new Set<string>();
    const suppliersSet = new Set<string>();

    financials.forEach(financial => {
      if (financial.fin_comm) communitiesSet.add(financial.fin_comm);
      if (financial.fin_supplier) suppliersSet.add(financial.fin_supplier);
    });

    return {
      communities: Array.from(communitiesSet).sort(),
      suppliers: Array.from(suppliersSet).sort()
    };
  }, [financials]);

  // Apply filters using the custom hook
  const filteredFinancials = useFinancialFilters(financials, searchQuery, filters);

  // Apply sorting
  const sortedFinancials = useMemo(() => {
    if (!filteredFinancials) return [];
    
    return [...filteredFinancials].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'id':
          return direction * ((a.fin_id || 0) - (b.fin_id || 0));
        case 'community':
          return direction * ((a.fin_comm || '').localeCompare(b.fin_comm || ''));
        case 'date':
          return direction * (new Date(a.fin_date).getTime() - new Date(b.fin_date).getTime());
        case 'income':
          return direction * ((a.fin_inc_amt || 0) - (b.fin_inc_amt || 0));
        case 'expense':
          return direction * ((a.fin_exp_amt || 0) - (b.fin_exp_amt || 0));
        case 'supplier':
          return direction * ((a.fin_supplier || '').localeCompare(b.fin_supplier || ''));
        case 'description':
          return direction * ((a.fin_desc || '').localeCompare(b.fin_desc || ''));
        default:
          return 0;
      }
    });
  }, [filteredFinancials, sortConfig]);

  // Apply pagination
  const paginatedFinancials = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedFinancials.slice(startIndex, endIndex);
  }, [sortedFinancials, currentPage, pageSize]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalIncome = filteredFinancials.reduce((sum, curr) => sum + (curr.fin_inc_amt || 0), 0);
    const avgIncomePerCommunity = totalIncome / (communities.length || 1);
    const totalExpenditure = filteredFinancials.reduce((sum, curr) => sum + (curr.fin_exp_amt || 0), 0);
    const avgExpPerCommunity = totalExpenditure / (communities.length || 1);

    return {
      totalIncome,
      avgIncomePerCommunity,
      totalExpenditure,
      avgExpPerCommunity
    };
  }, [filteredFinancials, communities.length]);

  // Map column IDs to sort fields
  const columnToSortFieldMap: Record<string, FinancialSortField> = {
    'fin_comm': 'community',
    'fin_date': 'date',
    'fin_inc_amt': 'income',
    'fin_exp_amt': 'expense',
    'fin_desc': 'description',
    'fin_supplier': 'supplier',
    'fin_id': 'id'
  };

  // Handle sorting
  const handleSort = (sortConfig: { field: string; direction: 'asc' | 'desc' }) => {
    console.log('Sorting:', sortConfig);
    
    // Map column ID to sort field
    const sortField = columnToSortFieldMap[sortConfig.field];
    
    // Only update sort config if we have a valid mapping
    if (sortField) {
      setSortConfig({
        field: sortField,
        direction: sortConfig.direction
      });
      
      console.log('Mapped sort config:', { field: sortField, direction: sortConfig.direction });
    } else {
      console.log('No mapping found for column:', sortConfig.field);
    }
  };

  // Handle sort button clicks
  const handleSortButtonClick = (field: FinancialSortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<FinancialFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle remove filter
  const handleRemoveFilter = (key: keyof FinancialFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Format currency
  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd/MM/yyyy');
  };

  // Handle edit modal
  const handleEditClick = (financial: DevFinancial) => {
    console.log('Edit clicked for financial record:', financial);
    setSelectedFinancial(financial);
    setIsEditModalOpen(true);
  };

  // Handle edit success
  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['financials'] });
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedFinancials.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Define table columns
  const columns = useMemo(() => {
    // Return the column definitions
    return [
      {
        id: 'actions',
        header: 'Edit',
        accessor: (row: DevFinancial) => (
          <div className="action-buttons">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(row);
              }}
              className="action-button edit"
              title="Edit Financial Record"
            >
              <Pencil className="h-5 w-5" />
            </button>
          </div>
        ),
        width: 'narrow' as ColumnWidth,
        visibleWhen: (permissions: any) => permissions.isAdmin || permissions.canManageSystemSettings
      },
      {
        id: 'fin_comm',
        header: 'Community',
        accessor: (row: DevFinancial) => (
          <div className="truncate" title={row.fin_comm || '-'}>
            {formatTextWithLinks(row.fin_comm || '-')}
          </div>
        ),
        width: 'medium' as ColumnWidth,
        sortable: true
      },
      {
        id: 'fin_date',
        header: 'Date',
        accessor: (row: DevFinancial) => (
          <div className="truncate">
            {formatDate(row.fin_date)}
          </div>
        ),
        width: 'medium' as ColumnWidth,
        sortable: true
      },
      {
        id: 'fin_inc_amt',
        header: 'Income',
        accessor: (row: DevFinancial) => (
          <div className="truncate text-right text-emerald-600 font-medium">
            {formatCurrency(row.fin_inc_amt)}
          </div>
        ),
        width: 'medium' as ColumnWidth,
        align: 'right' as 'right',
        sortable: true
      },
      {
        id: 'fin_exp_amt',
        header: 'Expense',
        accessor: (row: DevFinancial) => (
          <div className="truncate text-right text-red-600 font-medium">
            {formatCurrency(row.fin_exp_amt)}
          </div>
        ),
        width: 'medium' as ColumnWidth,
        align: 'right' as 'right',
        sortable: true
      },
      {
        id: 'fin_desc',
        header: 'Description',
        accessor: (row: DevFinancial) => (
          <div className="truncate" title={row.fin_desc || '-'}>
            {formatTextWithLinks(row.fin_desc || '-')}
          </div>
        ),
        width: 'wide' as ColumnWidth,
        sortable: true
      },
      {
        id: 'fin_exp_proj',
        header: 'Project',
        accessor: (row: DevFinancial) => (
          <div className="truncate" title={row.fin_exp_proj || '-'}>
            {formatTextWithLinks(row.fin_exp_proj || '-')}
          </div>
        ),
        width: 'medium' as ColumnWidth,
        sortable: true
      },
      {
        id: 'fin_supplier',
        header: 'Supplier',
        accessor: (row: DevFinancial) => (
          <div className="truncate" title={row.fin_supplier || '-'}>
            {formatTextWithLinks(row.fin_supplier || '-')}
          </div>
        ),
        width: 'medium' as ColumnWidth,
        sortable: true
      }
    ] as TableColumn<DevFinancial>[];
  }, [formatCurrency, formatDate, formatTextWithLinks]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading financial data. Please try again later.
      </div>
    );
  }

  return (
    <div className="p-4 h-[calc(100vh-6rem)] overflow-y-hidden">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Financial Analysis</h1>
          
          {/* Add buttons - only visible to users with admin privileges */}
          {(isAdmin || canManageSystemSettings) && (
            <div className="flex space-x-3">
              <button 
                onClick={() => setIsAddIncomeModalOpen(true)}
                className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Income
              </button>
              <button 
                onClick={() => setIsAddExpenseModalOpen(true)}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Expense
              </button>
            </div>
          )}
        </div>

        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="financial-summary-card income">
            <div className="financial-summary-label">Total Income</div>
            <div className="financial-summary-value income">{formatCurrency(summaryStats.totalIncome)}</div>
          </div>
          <div className="financial-summary-card income">
            <div className="financial-summary-label">Avg Income per Community</div>
            <div className="financial-summary-value income">{formatCurrency(summaryStats.avgIncomePerCommunity)}</div>
          </div>
          <div className="financial-summary-card expense">
            <div className="financial-summary-label">Total Expenditure</div>
            <div className="financial-summary-value expense">{formatCurrency(summaryStats.totalExpenditure)}</div>
          </div>
          <div className="financial-summary-card expense">
            <div className="financial-summary-label">Avg Expenditure per Community</div>
            <div className="financial-summary-value expense">{formatCurrency(summaryStats.avgExpPerCommunity)}</div>
          </div>
        </div>

        {/* Financial Year Selector */}
        <div className="financial-year-selector">
          <label htmlFor="financial-year">Financial Year</label>
          <select
            id="financial-year"
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
            className="max-w-xs"
          >
            {financialYearOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          {/* Search input */}
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search financials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          
          {/* Filter button */}
          <button
            className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            title="Toggle filters"
          >
            <Filter className="h-5 w-5 text-gray-500" />
            <span className="hidden sm:inline">Filters</span>
            {Object.values(filters).filter(Boolean).length > 0 && (
              <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                {Object.values(filters).filter(Boolean).length}
              </span>
            )}
          </button>
          
          <div className="text-base font-bold text-gray-700">
            Total Records: {filteredFinancials.length}
          </div>
        </div>

        {/* Active filters */}
        {Object.values(filters).some(Boolean) && (
          <div className="active-filters">
            {filters.community && (
              <div className="filter-tag">
                <span className="filter-tag-label">Community:</span>
                <span>{filters.community}</span>
                <button
                  onClick={() => handleRemoveFilter('community')}
                  className="filter-tag-remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {filters.supplier && (
              <div className="filter-tag">
                <span className="filter-tag-label">Supplier:</span>
                <span>{filters.supplier}</span>
                <button
                  onClick={() => handleRemoveFilter('supplier')}
                  className="filter-tag-remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {filters.dateFrom && (
              <div className="filter-tag">
                <span className="filter-tag-label">From:</span>
                <span>{format(filters.dateFrom, 'dd/MM/yyyy')}</span>
                <button
                  onClick={() => handleRemoveFilter('dateFrom')}
                  className="filter-tag-remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {filters.dateTo && (
              <div className="filter-tag">
                <span className="filter-tag-label">To:</span>
                <span>{format(filters.dateTo, 'dd/MM/yyyy')}</span>
                <button
                  onClick={() => handleRemoveFilter('dateTo')}
                  className="filter-tag-remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Filter panel */}
        {isFilterOpen && (
          <div className="filter-panel">
            <div className="filter-panel-header">
              <h3 className="font-medium text-gray-700">Filters</h3>
              <button
                onClick={() => setFilters({
                  community: null,
                  supplier: null,
                  dateFrom: null,
                  dateTo: null
                })}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear all
              </button>
            </div>
            <div className="filter-panel-body">
              {/* Community filter */}
              <div className="filter-group">
                <label htmlFor="community-filter">Community</label>
                <select
                  id="community-filter"
                  value={filters.community || ''}
                  onChange={(e) => handleFilterChange({ community: e.target.value || null })}
                  className="border border-gray-300 rounded-md p-2 text-sm"
                >
                  <option value="">All communities</option>
                  {communities.map(community => (
                    <option key={community} value={community}>{community}</option>
                  ))}
                </select>
              </div>
              
              {/* Supplier filter */}
              <div className="filter-group">
                <label htmlFor="supplier-filter">Supplier</label>
                <select
                  id="supplier-filter"
                  value={filters.supplier || ''}
                  onChange={(e) => handleFilterChange({ supplier: e.target.value || null })}
                  className="border border-gray-300 rounded-md p-2 text-sm"
                >
                  <option value="">All suppliers</option>
                  {suppliers.map(supplier => (
                    <option key={supplier} value={supplier}>{supplier}</option>
                  ))}
                </select>
              </div>
              
              {/* Date range filters */}
              <div className="filter-group">
                <label htmlFor="date-from-filter">Date From</label>
                <input
                  id="date-from-filter"
                  type="date"
                  value={filters.dateFrom?.toISOString().split('T')[0] || ''}
                  onChange={(e) => handleFilterChange({ 
                    dateFrom: e.target.value ? new Date(e.target.value) : null 
                  })}
                  className="border border-gray-300 rounded-md p-2 text-sm"
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="date-to-filter">Date To</label>
                <input
                  id="date-to-filter"
                  type="date"
                  value={filters.dateTo?.toISOString().split('T')[0] || ''}
                  onChange={(e) => handleFilterChange({ 
                    dateTo: e.target.value ? new Date(e.target.value) : null 
                  })}
                  className="border border-gray-300 rounded-md p-2 text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="mt-4">
        <EnhancedFinancialsTable
          columns={columns}
          data={paginatedFinancials}
          keyField="fin_id"
          width="2500px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading}
          emptyMessage="No financial records found"
          horizontalScroll={true}
          className="enhanced-table-force-scroll"
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
          permissions={{
            isAdmin,
            canManageSystemSettings
          }}
        />
      </div>

      {/* Edit Financial Modal */}
      {isEditModalOpen && selectedFinancial && (
        <EditFinancialModal
          isOpen={true}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedFinancial(null); // Reset selected financial when modal is closed
          }}
          onSuccess={handleEditSuccess}
          financial={selectedFinancial}
        />
      )}

      {/* Add Income Modal */}
      <AddIncomeModal
        isOpen={isAddIncomeModalOpen}
        onClose={() => setIsAddIncomeModalOpen(false)}
        onSuccess={handleEditSuccess}
      />
      
      {/* Add Expense Modal */}
      <AddExpenseModal
        isOpen={isAddExpenseModalOpen}
        onClose={() => setIsAddExpenseModalOpen(false)}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
}
